import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
} from '@mui/joy';
import DOMPurify from 'dompurify';
import { applyEmbedBrandTheme, reportEmbedState } from '../../lib/embedTheme';

import type { PublicReview as Review, PublicWidgetConfig as WidgetConfig } from '../../types/reviews';
import { ReviewWidgetRenderer } from '../../components/marketing/reviews/ReviewWidgetRenderer';

/**
 * Sanitize custom CSS to prevent XSS and CSS injection attacks.
 * Uses DOMPurify combined with CSS-specific pattern filtering for security.
 *
 * @param cssString - Raw CSS string from user input
 * @returns Sanitized CSS string safe for injection
 */
function sanitizeCustomCSS(cssString: string): string {
  if (!cssString || typeof cssString !== 'string') {
    return '';
  }

  // First, use DOMPurify to sanitize any HTML/script content
  const styleTag = `<style>${cssString}</style>`;
  const sanitized = DOMPurify.sanitize(styleTag, {
    ALLOWED_TAGS: ['style'],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });

  // Extract the CSS content from the sanitized style tag
  const match = sanitized.match(/<style>(.*?)<\/style>/s);
  let cssContent = match ? match[1] : '';

  // Additional CSS-specific sanitization: block dangerous patterns
  // DOMPurify's KEEP_CONTENT doesn't sanitize CSS-specific attacks
  const dangerousPatterns = [
    /javascript:/gi,           // JavaScript URLs
    /expression\s*\(/gi,       // IE expression()
    /@import/gi,               // @import rules (can load external resources)
    /@font-face/gi,            // @font-face (can load external fonts)
    /-moz-binding/gi,          // Firefox binding (old XSS vector)
    /behavior\s*:/gi,          // IE behavior property
    /<script/gi,               // Script tags (shouldn't be in CSS but block anyway)
    /on\w+\s*=/gi,             // Event handlers (shouldn't be in CSS)
  ];

  // Remove all dangerous patterns
  for (const pattern of dangerousPatterns) {
    cssContent = cssContent.replace(pattern, '');
  }

  return cssContent;
}

export default function ReviewWidgetDisplay() {
  const { widgetId } = useParams<{ widgetId: string }>();
  const [widget, setWidget] = useState<WidgetConfig | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    applyEmbedBrandTheme();
    fetchWidgetData();
  }, [widgetId]);

  // Tell the embedding page how tall we are and how many reviews we have —
  // generated websites hide the reviews section entirely while count is 0.
  useEffect(() => {
    if (loading || !widgetId) return;
    return reportEmbedState({
      type: 'apexos-reviews-state',
      widgetId,
      count: reviews.length,
    });
  }, [loading, reviews.length, widgetId]);

  useEffect(() => {
    if (!widget?.custom_css) return;
    const style = document.createElement('style');
    style.textContent = sanitizeCustomCSS(widget.custom_css);
    document.head.appendChild(style);
    return () => style.remove();
  }, [widget?.custom_css]);

  const fetchWidgetData = async () => {
    try {
      // Fetch widget configuration and reviews (public endpoint - no auth required)
      const response = await fetch(`/api/marketing/reviews/public/widget/${widgetId}/`);
      if (!response.ok) {
        throw new Error('Widget not found or not available');
      }
      const data = await response.json();
      setWidget(data.widget);
      setReviews(data.reviews);
    } catch (error) {
      console.error('Error fetching widget:', error);
      setError('Unable to load reviews widget');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box id="apexos-embed-content" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 160 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">{error}</Alert>
      </Box>
    );
  }

  if (!widget || reviews.length === 0) {
    // Render nothing: when embedded, the parent page hides the section
    // (via the reviews-state message); a standalone visit just sees an
    // empty page rather than an error-looking alert.
    return null;
  }

  return <Box id="apexos-embed-content"><ReviewWidgetRenderer reviews={reviews} config={widget} /></Box>;
}
